import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

const formatDuration = (seconds) => (seconds ? `${Math.round(seconds / 60)} min` : "");

const formatTime = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const EpisodeCard = ({ episode, index, isPlaying, onPlay, onStop }) => {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadedDuration, setLoadedDuration] = useState(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.play().catch(() => onStop());
    } else {
      audio.pause();
    }
  }, [isPlaying, onStop]);

  const duration = loadedDuration || episode.duration_seconds || 0;
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const started = currentTime > 0;

  return (
    <article
      className="bg-[#02515A] texture-teal p-6 md:p-8 flex flex-col sm:flex-row gap-6 sm:gap-8"
      data-testid={`episode-card-${index + 1}`}
    >
      <img
        src={episode.image || "/images/goodbye-moon.png"}
        alt={`${episode.title} cover art`}
        loading="lazy"
        className="w-24 h-24 md:w-32 md:h-32 object-cover shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] tracking-[0.25em] uppercase text-[#E0A8C5]">
          {formatDate(episode.date)}
          {episode.duration_seconds ? ` · ${formatDuration(episode.duration_seconds)}` : ""}
        </p>
        <h3 className="mt-3 font-serif-display font-light text-2xl md:text-3xl leading-snug text-[#FCF5EB]">
          {episode.title}
        </h3>
        {episode.summary && (
          <p className="mt-3 text-sm font-light leading-relaxed text-[#FCF5EB]/70 line-clamp-2">
            {episode.summary}
          </p>
        )}
        <div className="mt-6 flex items-center gap-5">
          <button
            onClick={() => (isPlaying ? onStop() : onPlay())}
            className="inline-flex items-center gap-3 bg-[#7D1B56] text-[#FCF5EB] px-6 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#FCF5EB] hover:text-[#02515A] transition-colors duration-300"
            data-testid={`episode-play-${index + 1}`}
            aria-label={isPlaying ? `Pause ${episode.title}` : `Play ${episode.title}`}
          >
            {isPlaying ? (
              <Pause className="w-3.5 h-3.5" strokeWidth={2} />
            ) : (
              <Play className="w-3.5 h-3.5" strokeWidth={2} />
            )}
            {isPlaying ? "Pause" : "Play"}
          </button>
          {(isPlaying || started) && (
            <span className="text-xs font-light text-[#FCF5EB]/60">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          )}
        </div>
        {(isPlaying || started) && (
          <div className="mt-4 h-px bg-[#FCF5EB]/20 w-full">
            <div
              className="h-px bg-[#D9A441] transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
      <audio
        ref={audioRef}
        src={episode.audio_url}
        preload="metadata"
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setLoadedDuration(e.currentTarget.duration)}
        onEnded={onStop}
        className="hidden"
        data-testid={`episode-audio-${index + 1}`}
      />
    </article>
  );
};
